import { Injectable } from '@angular/core';
import { Observable, map, switchMap, of, BehaviorSubject, throwError } from 'rxjs';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { ServerLinks } from '@shared/services/server-links';
import { ServerLinkName } from '@shared/models/server-links-model';
import {
  DictionaryValuesResponseArray,
  Categories,
  CatalogItemValueResponseArray,
  RecommendationForCatalogItemsResponse,
  NoticeCountView,
  NoticeCountResponse
} from '@shared/models/shared.service';
import { environment } from '@environments/environment';
import { Stend } from '@shared/models/Stend';
import {
  ProfileTabInfo,
  UnomAdress,
  UserApiKeyResponse,
  UserDataProfilesHeader,
  UserDataProfilesResponse
} from '../../personal-profile/models/user.service';
import { MobileApp, PopularApp, PopularAppsResponse } from '../../main/models/mobile-apps';
import { Providers } from '../../data/modules/catalog-data/models/catalog.service';
import { DateTime } from 'luxon';
import { Gender } from '@auth/models/auth.state';
import { petsOptions } from '@shared/models/pets';
import { catchError } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class SharedService {
  public userDataProfiles$ = new BehaviorSubject<UserDataProfilesHeader | null>(null);
  public userProfileTab$ =  new BehaviorSubject<ProfileTabInfo | null>(null);
  public loadingDataProfiles$ = new BehaviorSubject<boolean>(false);

  private _urlopenData = '';
  private _urlLk = '';
  private _urlEhdapi = '';
  private _fileUrl = '';
  private _catalogId: number;
  private _popularAppCatalogId: number;
  private readonly _httpHeaders = new HttpHeaders({
    'Content-Type': 'application/json',
    'ehd-system': 'opod'
  });

  constructor(private http: HttpClient, serverLinks: ServerLinks) {
    this._urlopenData = serverLinks.getLink(ServerLinkName.OpenData);
    this._urlEhdapi = serverLinks.getLink(ServerLinkName.EhdApi);
    this._urlLk = serverLinks.getLink(ServerLinkName.Lk);
    this._fileUrl = serverLinks.getLink(ServerLinkName.File);

    switch (environment.stend) {
      case Stend.Dev:
        this._catalogId = 87406;
        break;
      case Stend.Stage:
        this._catalogId = 6404;
        break;
      case Stend.Prod:
        this._catalogId = 29580;
        break;
      default:
        this._catalogId = 87406;
    }
  }

  private getAppIconSrcOrFallback(icon: string | null): string {
    return icon ? `${this._fileUrl}${icon}` : 'https://www.freepngimg.com/thumb/iphone/80146-blue-apple-text-app-iphone-store.png';
  }

  public getProviders(): Observable<Providers[]> {
    const body = {
      sorting: {
        byNamePD: 'ASC'
      }
    };
    return this.http.post<Providers[]>(`${this._urlopenData}oiv/list`, body);
  }

  public getCategories(): Observable<Categories[]> {
    const body = {
      isDynamic: true,
      publicationStatus: 'Опубликована',
      sorting: {
        byNameOD: 'ASC'
      }
    };
    return this.http.post<Categories[]>(`${this._urlopenData}category/list/`, body);
  }

  public getDictionaryValues(dictionaryId: number): Observable<DictionaryValuesResponseArray> {
    const body = {
      id: dictionaryId
    };
    return this.http.post<CatalogItemValueResponseArray>(`${this._urlEhdapi}dictionary/get`, body, {
      headers: this._httpHeaders
    });
  }

  public getRecommendationForCatalogItems(catalogId: number): Observable<RecommendationForCatalogItemsResponse> {
    return this.http.get<RecommendationForCatalogItemsResponse>(`${this._urlLk}api/v1/recommendation?idCatalogs=${catalogId}`, {
      headers: this._httpHeaders
    });
  }

  public getNoticeCount(): Observable<NoticeCountView> {
    return this.http
      .get<NoticeCountResponse>(`${this._urlLk}api/v1/notices/count`, {
        headers: this._httpHeaders
      })
      .pipe(
        map(res => ({
          all: res.count_1 + res.count_0,
          unread: res.count_0
        }))
      );
  }

  public allNotificationRead(): Observable<never> {
    const body = {
      idsNotices: []
    };
    return this.http.post<never>(`${this._urlLk}api/v1/notices/status`, body, {
      headers: this._httpHeaders
    });
  }

  public getUserApiKey(): Observable<string | null> {
    const url = `${this._urlLk}api/v1/apiKey`;
    return this.http.get<UserApiKeyResponse>(url).pipe(map(res => res?.api_key));
  }

  public createUserApiKey(): Observable<string | null> {
    const url = `${this._urlLk}api/v1/apiKey/?generate=true`;
    return this.http.get<UserApiKeyResponse>(url).pipe(map(res => res?.api_key));
  }

  public getPopularApps(): Observable<MobileApp[]> {
    const popularAppCatalogId = environment.popularAppCatalogId;
    const body = { id: popularAppCatalogId, criteria: `Public_status = 'Опубликовано' and is_deleted=0`, sorting: { Name: 'ASC' } };

    return this.http.post<PopularAppsResponse>(`${this._urlEhdapi}catalog/get/`, body, { headers: this._httpHeaders }).pipe(
      map(res => res.response),
      map(response => this.parsePopularApps(response)),
    );
  }

  private parsePopularApps(popularApps: PopularApp[] = []): MobileApp[] {
    return popularApps?.map(app => {
      const appStores = app.Platform_data.map(platform => ({
        name: platform.Platform,
        platform: platform.Platform,
        link: platform.Link,
        store: platform.applic_store,
      }));

      return {
        description: app.Description,
        id: app.global_id,
        icon: this.getAppIconSrcOrFallback(app.Icon_app),
        name: app.Name,
        appStores
      };
    })
  }

  public getUserDataProfiles(): Observable<{
    userDataProfiles: UserDataProfilesHeader,
    userProfileTab: ProfileTabInfo
  }> {
    this.loadingDataProfiles$.next(true);
    return this.http.get<UserDataProfilesResponse>(`${this._urlLk}api/v1/dataProfiles`)
      .pipe(
        switchMap(profile => {
          const parsedProfile = this.parseUserProfile(profile);
          const { userDataProfiles, userProfileTab } = parsedProfile;

          if (userProfileTab.addresses.jobAddress.unom || userProfileTab.addresses.livAddress.unom) {
            const unomIds = [userProfileTab.addresses.jobAddress.unom, userProfileTab.addresses.livAddress.unom].filter(x => x) as string[];
            return this.getStreetName(unomIds).pipe(
              map(res => {
                if (res.response) {
                  const liveAddress = userProfileTab.addresses.livAddress;
                  liveAddress.address = res.response.find(x => x.UNOM === +(liveAddress.unom ?? 0))?.ADDRESS ?? null;
                  const jovAddress = userProfileTab.addresses.jobAddress;
                  jovAddress.address = res.response.find(x => x.UNOM === +(jovAddress.unom ?? 0))?.ADDRESS ?? null;
                }

                this.userDataProfiles$.next(userDataProfiles);
                this.userProfileTab$.next(userProfileTab);
                this.loadingDataProfiles$.next(false);
                return { userDataProfiles, userProfileTab };
              })
            );
          }

          this.userDataProfiles$.next(userDataProfiles);
          this.userProfileTab$.next(userProfileTab);
          this.loadingDataProfiles$.next(false);
          return of(parsedProfile);
        }),
        catchError(e => {
          this.userDataProfiles$.next(null);
          this.userProfileTab$.next(null);
          this.loadingDataProfiles$.next(false);
          return throwError(() => e);
        }),
      );
  }

  public getStreetName(unomIds: string[]): Observable<UnomAdress> {
    const body = { id: this._catalogId, criteria: `UNOM in [${unomIds.toString()}]`, projection: ['UNOM', 'ADDRESS'] };
    return this.http.post<UnomAdress>(`${this._urlEhdapi}catalog/get/`, body, { headers: this._httpHeaders });
  }

  private parseUserProfile(
    data: UserDataProfilesResponse
  ): {
    userDataProfiles: UserDataProfilesHeader,
    userProfileTab: ProfileTabInfo,
  } {
    const userDataProfiles: UserDataProfilesHeader = {
      contactEmail: data?.contacts?.contact_email?.[0]?.value ?? 'OTHER.NOT_DATA',
      birthDate: isNaN(new Date(data.birth_date).getDay())
        ? 'OTHER.NOT_DATA'
        : DateTime.fromJSDate(new Date(data.birth_date)).toFormat('dd.MM.yyyy'),
      gender: data.gender
        ? (data.gender === Gender.Man ? Gender.Man : Gender.Female)
        : null,
      notFoundData: false
    };

    const userProfileTab: ProfileTabInfo = {
      pets: data?.pets?.map(res => ({
        birthDate: isNaN(new Date(res.birth_date).getDay())
          ? 'OTHER.NOT_DATA'
          : DateTime.fromJSDate(new Date(res.birth_date)).toFormat('dd.MM.yyyy'),
        species: petsOptions.find(x => x.id === +res.species)?.name ?? 'OTHER.NOT_DATA'
      })),
      cards: {
        troykaUid: data?.cards?.card_troyka?.[0].mdm_obj_id ?? null
      },
      documents: {
        osago: data.documents?.osago?.map(x => ({ expiryDate: DateTime.fromJSDate(new Date(x.expiry_date)).toFormat('dd.MM.yyyy') })) ?? []
      },
      addresses: {
        jobAddress: {
          unom: data?.addresses?.addr_job ? data.addresses.addr_job?.[0].unom ?? null : null,
          address: null
        },
        livAddress: {
          unom: data?.addresses?.addr_live ? data?.addresses?.addr_live?.[0].unom ?? null : null,
          address: null
        }
      }
    };

    return { userDataProfiles, userProfileTab };
  }
}
